/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var lastTrigger = null;
  var activeDialog = null;
  var restoreTarget = null;
  var focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(',');

  function isVisible(element) {
    if (!element) {
      return false;
    }

    var style = window.getComputedStyle(element);
    var rect = element.getBoundingClientRect();

    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && rect.width > 0 && rect.height > 0;
  }

  function getFocusable(dialog) {
    return Array.prototype.slice.call(dialog.querySelectorAll(focusableSelector)).filter(function (element) {
      return isVisible(element) && !element.hasAttribute('inert') && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function inferFrameTitle(frame) {
    var source = (frame.getAttribute('src') || '').toLowerCase();
    var signature = getControlSignature(frame);
    var text = [source, signature].join(' ');

    if (text.indexOf('youtube') !== -1 || text.indexOf('vimeo') !== -1 || text.indexOf('video') !== -1) {
      return 'Embedded video';
    }

    if (text.indexOf('quiz') !== -1 || text.indexOf('product-finder') !== -1 || text.indexOf('perfect-product-finder') !== -1) {
      return 'Product finder quiz';
    }

    if (text.indexOf('review') !== -1 || text.indexOf('stamped') !== -1) {
      return 'Product reviews';
    }

    return 'Embedded content';
  }

  function patchFrameTitles(root) {
    Array.prototype.forEach.call(root.querySelectorAll('iframe'), function (frame) {
      if ((frame.getAttribute('title') || '').trim()) {
        return;
      }

      var dialog = frame.closest('[role="dialog"], dialog');
      var label = dialog && (dialog.getAttribute('aria-label') || dialog.getAttribute('aria-labelledby'));

      frame.setAttribute('title', label || inferFrameTitle(frame));
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function applyReducedMotion(root) {
    if (!prefersReducedMotion()) {
      return;
    }

    Array.prototype.forEach.call(root.querySelectorAll('video[autoplay]'), function (video) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    });

    Array.prototype.forEach.call(root.querySelectorAll('iframe[src*="autoplay=1"]'), function (frame) {
      frame.src = frame.src.replace(/autoplay=1/g, 'autoplay=0');
    });
  }

  function toPositiveNumber(value) {
    var number = parseFloat(value);

    return isFinite(number) && number > 0 ? number : 0;
  }

  function imageFileLooksLikeSpacer(image) {
    var source = image.currentSrc || image.src || image.getAttribute('src') || '';
    var fileName = source.split('?')[0].split('#')[0].split('/').pop().toLowerCase();

    return /^(spacer|space|blank)(\.|_|-|$)/.test(fileName);
  }

  function imageHasTinyDimension(image) {
    var attrWidth = toPositiveNumber(image.getAttribute('width'));
    var attrHeight = toPositiveNumber(image.getAttribute('height'));

    if ((attrWidth && attrWidth <= 3) || (attrHeight && attrHeight <= 3)) {
      return true;
    }

    if ((image.naturalWidth && image.naturalWidth <= 3) || (image.naturalHeight && image.naturalHeight <= 3)) {
      return true;
    }

    if (image.getBoundingClientRect) {
      var rect = image.getBoundingClientRect();

      return (rect.width > 0 && rect.width <= 3) || (rect.height > 0 && rect.height <= 3);
    }

    return false;
  }

  function markImageAsSpacer(image) {
    image.setAttribute('alt', '');
    image.setAttribute('aria-hidden', 'true');
  }

  function watchSpacerImageLoad(image) {
    if (image.complete || image._sandmarcA11ySpacerWatch) {
      return;
    }

    image._sandmarcA11ySpacerWatch = true;
    image.addEventListener('load', function () {
      if (!image.hasAttribute('alt') && (imageFileLooksLikeSpacer(image) || imageHasTinyDimension(image))) {
        markImageAsSpacer(image);
      }
    }, { once: true });
  }

  function patchSpacerImages(root) {
    Array.prototype.forEach.call(root.querySelectorAll('img:not([alt])'), function (image) {
      if (!imageFileLooksLikeSpacer(image) && !imageHasTinyDimension(image)) {
        watchSpacerImageLoad(image);
        return;
      }

      markImageAsSpacer(image);
    });
  }

  function getElementClassName(element) {
    if (!element) {
      return '';
    }

    if (typeof element.className === 'string') {
      return element.className;
    }

    return element.getAttribute && element.getAttribute('class') || '';
  }

  function getControlSignature(element) {
    var tokens = [];
    var current = element;
    var depth = 0;

    while (current && current !== document.documentElement && depth < 8) {
      tokens.push(current.id || '');
      tokens.push(getElementClassName(current));
      tokens.push(current.getAttribute && current.getAttribute('role') || '');
      tokens.push(current.getAttribute && current.getAttribute('aria-label') || '');
      tokens.push(current.getAttribute && current.getAttribute('data-gallery-id') ? 'gallery' : '');
      tokens.push(current.getAttribute && current.getAttribute('data-covet') !== null ? 'covet' : '');
      current = current.parentElement;
      depth += 1;
    }

    Array.prototype.forEach.call(element.querySelectorAll('[class], use'), function (child) {
      tokens.push(getElementClassName(child));
      tokens.push(child.getAttribute('href') || '');
      tokens.push(child.getAttribute('xlink:href') || '');
    });

    return tokens.join(' ').toLowerCase();
  }

  function hasAccessibleName(button) {
    var labelledBy = (button.getAttribute('aria-labelledby') || '').trim();

    if ((button.getAttribute('aria-label') || '').trim() || (button.getAttribute('title') || '').trim()) {
      return true;
    }

    if (labelledBy) {
      var labelIds = labelledBy.split(/\s+/);
      var hasLabelText = labelIds.some(function (id) {
        var label = document.getElementById(id);

        return label && (label.textContent || '').trim();
      });

      if (hasLabelText) {
        return true;
      }
    }

    if ((button.textContent || '').trim()) {
      return true;
    }

    return Array.prototype.some.call(button.querySelectorAll('img[alt]'), function (image) {
      return (image.getAttribute('alt') || '').trim();
    });
  }

  function isGeneratedGalleryControl(signature) {
    return /(covet|instagram|gallery|carousel|slider|swiper|slick|flickity|glide|splide|instant)/.test(signature);
  }

  function inferControlLabel(button, signature) {
    if (/(^|\s|_|-)(prev|previous|arrow-left|chevron-left|left)(\s|_|-|$)/.test(signature)) {
      return 'Previous item';
    }

    if (/(^|\s|_|-)(next|arrow-right|chevron-right|right)(\s|_|-|$)/.test(signature)) {
      return 'Next item';
    }

    if (/(^|\s|_|-)(close|dismiss)(\s|_|-|$)/.test(signature)) {
      return 'Close';
    }

    if (/(^|\s|_|-)pause(\s|_|-|$)/.test(signature)) {
      return 'Pause';
    }

    if (/(^|\s|_|-)play(\s|_|-|$)/.test(signature)) {
      return 'Play';
    }

    if (isGeneratedGalleryControl(signature) && button.parentElement) {
      var siblingButtons = Array.prototype.filter.call(button.parentElement.querySelectorAll('button'), function (sibling) {
        return isVisible(sibling);
      });
      var siblingIndex = siblingButtons.indexOf(button);

      if (siblingButtons.length === 2 && siblingIndex !== -1) {
        return siblingIndex === 0 ? 'Previous item' : 'Next item';
      }
    }

    return isGeneratedGalleryControl(signature) ? 'Gallery control' : '';
  }

  function inferInputButtonValue(input, signature) {
    var type = (input.getAttribute('type') || '').toLowerCase();
    var text = [
      input.id || '',
      input.name || '',
      getElementClassName(input),
      signature
    ].join(' ').toLowerCase();

    if (text.indexOf('search') !== -1) {
      return 'Search';
    }

    if (text.indexOf('newsletter') !== -1 || text.indexOf('email') !== -1 || text.indexOf('subscribe') !== -1) {
      return 'Subscribe';
    }

    if (text.indexOf('review') !== -1 || text.indexOf('stamped') !== -1) {
      return 'Submit review';
    }

    if (text.indexOf('cart') !== -1 || text.indexOf('checkout') !== -1 || text.indexOf('payment') !== -1) {
      return 'Continue';
    }

    if (text.indexOf('close') !== -1 || text.indexOf('dismiss') !== -1) {
      return 'Close';
    }

    if (type === 'reset') {
      return 'Reset';
    }

    return type === 'submit' ? 'Submit' : 'Button';
  }

  function patchEmptyGeneratedButtons(root) {
    Array.prototype.forEach.call(root.querySelectorAll('button'), function (button) {
      if (hasAccessibleName(button)) {
        return;
      }

      var signature = getControlSignature(button);
      var label = inferControlLabel(button, signature);

      button.setAttribute('aria-label', label || 'Button');

      if (button.closest && !button.closest('form') && !button.hasAttribute('type')) {
        button.setAttribute('type', 'button');
      }
    });

    Array.prototype.forEach.call(root.querySelectorAll('input[type="submit"], input[type="button"], input[type="reset"]'), function (input) {
      if ((input.getAttribute('value') || '').trim()) {
        return;
      }

      input.setAttribute('value', inferInputButtonValue(input, getControlSignature(input)));
    });
  }

  function hasLinkAccessibleName(link) {
    var labelledBy = (link.getAttribute('aria-labelledby') || '').trim();

    if ((link.getAttribute('aria-label') || '').trim() || (link.getAttribute('title') || '').trim()) {
      return true;
    }

    if (labelledBy) {
      var labelIds = labelledBy.split(/\s+/);
      var hasLabelText = labelIds.some(function (id) {
        var label = document.getElementById(id);

        return label && (label.textContent || '').trim();
      });

      if (hasLabelText) {
        return true;
      }
    }

    if ((link.textContent || '').trim()) {
      return true;
    }

    return Array.prototype.some.call(link.querySelectorAll('img[alt]'), function (image) {
      return (image.getAttribute('alt') || '').trim();
    });
  }

  function inferLinkLabel(link, signature) {
    var href = (link.getAttribute('href') || '').toLowerCase();
    var text = [href, signature].join(' ');

    if (text.indexOf('review') !== -1 || text.indexOf('stamped') !== -1) {
      return 'Read product reviews';
    }

    if (text.indexOf('instagram') !== -1 || text.indexOf('covet') !== -1 || text.indexOf('gallery') !== -1) {
      return 'Open gallery item';
    }

    if (text.indexOf('slider') !== -1 || text.indexOf('carousel') !== -1 || text.indexOf('instant') !== -1) {
      return 'Open item';
    }

    if (href.indexOf('#product-') !== -1 && (href.indexOf('reviews') !== -1 || href.indexOf('tabs') !== -1)) {
      return 'Read product reviews';
    }

    if (href && href.charAt(0) !== '#' && href.indexOf('javascript:') !== 0) {
      var path = href.split('?')[0].split('#')[0].replace(/^https?:\/\/[^/]+/i, '').replace(/\/$/, '');
      var slug = path.split('/').filter(Boolean).pop();

      if (slug) {
        return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (letter) {
          return letter.toUpperCase();
        });
      }
    }

    return '';
  }

  function patchEmptyGeneratedLinks(root) {
    Array.prototype.forEach.call(root.querySelectorAll('a[href]'), function (link) {
      if (hasLinkAccessibleName(link)) {
        return;
      }

      var signature = getControlSignature(link);
      var label = inferLinkLabel(link, signature);

      if (!label) {
        var href = (link.getAttribute('href') || '').trim();

        if (!href || href === '#' || href.toLowerCase().indexOf('javascript:') === 0) {
          link.removeAttribute('href');
          link.setAttribute('aria-hidden', 'true');
          link.setAttribute('tabindex', '-1');
        }

        return;
      }

      link.setAttribute('aria-label', label);
    });
  }

  function hasFormControlLabel(control) {
    var labelledBy = (control.getAttribute('aria-labelledby') || '').trim();

    if ((control.getAttribute('aria-label') || '').trim() || (control.getAttribute('title') || '').trim()) {
      return true;
    }

    if (labelledBy) {
      var labelIds = labelledBy.split(/\s+/);
      var hasLabelText = labelIds.some(function (id) {
        var label = document.getElementById(id);

        return label && (label.textContent || '').trim();
      });

      if (hasLabelText) {
        return true;
      }
    }

    if (control.labels && control.labels.length > 0) {
      return true;
    }

    return Boolean(control.closest && control.closest('label'));
  }

  function isGeneratedAppFormControl(signature) {
    return /(stamped|shopify-payment|payment-terms|product-review|spr-|spr_|covet|gorgias|klaviyo|bundle-builder|app-block)/.test(signature);
  }

  function inferFormControlLabel(control, signature) {
    var type = (control.getAttribute('type') || '').toLowerCase();
    var name = (control.getAttribute('name') || '').toLowerCase();
    var id = (control.id || '').toLowerCase();
    var placeholder = (control.getAttribute('placeholder') || '').trim();
    var text = [type, name, id, signature].join(' ');

    if (placeholder) {
      return placeholder;
    }

    if (type === 'email' || text.indexOf('email') !== -1 || text.indexOf('newsletter') !== -1) {
      return 'Email address';
    }

    if (type === 'search' || text.indexOf('search') !== -1) {
      return 'Search';
    }

    if (type === 'tel' || text.indexOf('phone') !== -1) {
      return 'Phone number';
    }

    if (control.tagName && control.tagName.toLowerCase() === 'select') {
      return text.indexOf('review') !== -1 || text.indexOf('stamped') !== -1 ? 'Review option' : 'Select an option';
    }

    if (type === 'checkbox' || type === 'radio') {
      return 'Option';
    }

    if (text.indexOf('review') !== -1 || text.indexOf('stamped') !== -1) {
      return 'Review field';
    }

    if (text.indexOf('payment') !== -1) {
      return 'Payment option';
    }

    return 'Form field';
  }

  function patchGeneratedFormControls(root) {
    Array.prototype.forEach.call(root.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]), select, textarea'), function (control) {
      if (hasFormControlLabel(control)) {
        return;
      }

      var signature = getControlSignature(control);

      control.setAttribute('aria-label', inferFormControlLabel(control, signature));
    });
  }

  function hasHeadingContent(heading) {
    var labelledBy = (heading.getAttribute('aria-labelledby') || '').trim();

    if ((heading.textContent || '').trim() || (heading.getAttribute('aria-label') || '').trim() || (heading.getAttribute('title') || '').trim()) {
      return true;
    }

    if (labelledBy) {
      var labelIds = labelledBy.split(/\s+/);
      var hasLabelText = labelIds.some(function (id) {
        var label = document.getElementById(id);

        return label && (label.textContent || '').trim();
      });

      if (hasLabelText) {
        return true;
      }
    }

    if (Array.prototype.some.call(heading.querySelectorAll('img[alt]'), function (image) {
      return (image.getAttribute('alt') || '').trim();
    })) {
      return true;
    }

    if (Array.prototype.some.call(heading.querySelectorAll('svg title'), function (title) {
      return (title.textContent || '').trim();
    })) {
      return true;
    }

    return false;
  }

  function hasContentBearingChildren(heading) {
    return Boolean(heading.querySelector('a[href], button, input, select, textarea, iframe, video, canvas, [tabindex]:not([tabindex="-1"]), [role]:not([role="presentation"]):not([role="none"])'));
  }

  function patchEmptyHeadings(root) {
    Array.prototype.forEach.call(root.querySelectorAll('h1, h2, h3, h4, h5, h6'), function (heading) {
      if (hasHeadingContent(heading) || hasContentBearingChildren(heading)) {
        return;
      }

      heading.remove();
    });
  }

  function patchGeneratedA11y(root) {
    patchSpacerImages(root);
    patchEmptyGeneratedButtons(root);
    patchEmptyGeneratedLinks(root);
    patchGeneratedFormControls(root);
    patchEmptyHeadings(root);
  }

  function getVisibleDialogs() {
    return Array.prototype.slice.call(document.querySelectorAll('[role="dialog"], dialog')).filter(isVisible);
  }

  function findCloseControl(dialog, focusable) {
    return focusable.filter(function (element) {
      var label = [
        element.getAttribute('aria-label') || '',
        element.getAttribute('title') || '',
        element.id || '',
        typeof element.className === 'string' ? element.className : '',
        element.textContent || ''
      ].join(' ').toLowerCase();

      return label.indexOf('close') !== -1 || label.indexOf('×') !== -1 || label.trim() === 'x';
    })[0];
  }

  function focusDialog(dialog) {
    var focusable = getFocusable(dialog);
    var preferred = findCloseControl(dialog, focusable);
    var target = preferred && isVisible(preferred) ? preferred : focusable[0];

    if (!target) {
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      target = dialog;
    }

    target.focus({ preventScroll: true });
  }

  function restoreFocus() {
    var target = restoreTarget || lastTrigger;

    if (target && document.contains(target) && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }

    restoreTarget = null;
  }

  function syncDialogs() {
    patchFrameTitles(document);
    applyReducedMotion(document);
    patchGeneratedA11y(document);

    var visibleDialogs = getVisibleDialogs();
    var nextDialog = visibleDialogs[0] || null;

    if (nextDialog && nextDialog !== activeDialog) {
      activeDialog = nextDialog;
      restoreTarget = lastTrigger && document.contains(lastTrigger) ? lastTrigger : document.activeElement;
      activeDialog.setAttribute('aria-modal', activeDialog.getAttribute('aria-modal') || 'true');
      focusDialog(activeDialog);
    } else if (!nextDialog && activeDialog) {
      activeDialog = null;
      restoreFocus();
    }
  }

  function trapFocus(event) {
    if (!activeDialog || !isVisible(activeDialog)) {
      syncDialogs();
    }

    if (!activeDialog || !isVisible(activeDialog)) {
      return;
    }

    if (event.key === 'Escape') {
      window.setTimeout(syncDialogs, 80);
      window.setTimeout(syncDialogs, 500);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    var focusable = getFocusable(activeDialog);

    if (focusable.length === 0) {
      event.preventDefault();
      activeDialog.focus({ preventScroll: true });
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('button, a, [role="button"]');

    if (trigger) {
      lastTrigger = trigger;
    }

    window.setTimeout(syncDialogs, 0);
    window.setTimeout(syncDialogs, 350);
  }, true);

  document.addEventListener('keydown', function (event) {
    if ((event.key === 'Enter' || event.key === ' ') && event.target && event.target.matches && event.target.matches('button, a, [role="button"]')) {
      lastTrigger = event.target;
    }

    trapFocus(event);
  }, true);

  if (prefersReducedMotion() && window.Element && Element.prototype.scrollIntoView) {
    var scrollIntoView = Element.prototype.scrollIntoView;

    Element.prototype.scrollIntoView = function (options) {
      if (options && typeof options === 'object' && options.behavior === 'smooth') {
        options = Object.assign({}, options, { behavior: 'auto' });
      }

      return scrollIntoView.call(this, options);
    };
  }

  if (window.MutationObserver) {
    new MutationObserver(syncDialogs).observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class', 'style', 'open', 'src', 'aria-hidden']
    });
  }

  document.addEventListener('DOMContentLoaded', syncDialogs);
  window.addEventListener('load', syncDialogs);
})();
